import { Injectable, inject } from '@angular/core';
import { 
  Firestore, 
  collectionData, 
  collection, 
  query, 
  orderBy, 
  doc, 
  updateDoc, 
  docData, 
  where, 
  getDocs, 
  Timestamp, 
  arrayUnion, 
  runTransaction
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Observable, from as fromPromise, map } from 'rxjs';
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  eachWeekOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  max,
  min,
  startOfDay,
  startOfMonth,
  startOfWeek
} from 'date-fns';
import type {
  CountGranularity,
  Order,
  OrderComment,
  OrderCountBucket,
  OrderCounts,
  OrderPlatform,
  PrintedOrdersQuery
} from './order.model';
import { PRINTED_STATUSES } from './order-status';
import { getSizeKey } from './size.utils';

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(Auth);

  getAllUserOrders(userId: string, companyId?: string): Observable<Order[]> {
    const ordersRef = collection(this.firestore, 'orders');
    const ordersQuery = query(
      ordersRef,
      where('userId', '==', userId),
      ...(companyId ? [where('companyId', '==', companyId)] : []),
      orderBy('lastUpdated', 'desc')
    );
    return collectionData(ordersQuery, { idField: 'id' }) as Observable<Order[]>;
  }

  getOrders(userId?: string): Observable<Order[]> {
    const statuses = ['waitingForApproval', 'processing', 'done'] as const;
    const ordersRef = collection(this.firestore, 'orders');
    
    const constraints = [
      where('status', 'in', statuses),
      orderBy('lastUpdated', 'desc')
    ];

    if (userId) {
      constraints.splice(2, 0, where('userId', '==', userId));
    }
    
    const ordersQuery = query(ordersRef, ...constraints);
    return collectionData(ordersQuery, { idField: 'id' }) as Observable<Order[]>;
  }

  getPrintedOrders(from: Date, to: Date): Observable<Order[]> {
    const ordersRef = collection(this.firestore, 'orders');
    const ordersQuery = query(
      ordersRef,
      where('status', 'in', PRINTED_STATUSES),
      where('lastUpdated', '>=', Timestamp.fromDate(startOfDay(from))),
      where('lastUpdated', '<', Timestamp.fromDate(startOfDay(addDays(to, 1)))),
      orderBy('lastUpdated', 'desc')
    );
    // One-shot read: a live listener emits cached results first and then the server
    // results, which made the statistics chart animate twice per filter change.
    return fromPromise(getDocs(ordersQuery)).pipe(
      map(snapshot => snapshot.docs.map(d => ({ id: d.id, ...d.data() }) as Order))
    );
  }

  async updateOrderStatus(orderId: string, status: string): Promise<void> {
    const orderDoc = doc(this.firestore, 'orders', orderId);
    await updateDoc(orderDoc, { status });
  }

  getOrder(orderId: string): Observable<Order> {
    const orderRef = doc(this.firestore, `orders/${orderId}`);
    return docData(orderRef, { idField: 'id' }) as Observable<Order>;
  }

  getTotalPhotos(order?: Order): number {
    return getTotalPhotos(order);
  }

  calculateTotalPrice(order?: Order): number {
    return calculateTotalPrice(order);
  }

  async addComment(orderId: string, message: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to add comments');
    }

    const now = Timestamp.now();
    const comment: OrderComment = {
      id: crypto.randomUUID(),
      userId: user.uid,
      userName: user.displayName ?? 'Anonymous',
      message: message.trim(),
      createdAt: now,
      updatedAt: now
    };

    const orderDoc = doc(this.firestore, 'orders', orderId);
    await updateDoc(orderDoc, {
      comments: arrayUnion(comment),
      [`commentsLastSeenBy.${user.uid}`]: Timestamp.now(),
      lastUpdated: Timestamp.now()
    });
  }

  async updateComment(orderId: string, commentId: string, newMessage: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to update comments');
    }

    const orderDoc = doc(this.firestore, 'orders', orderId);

    await runTransaction(this.firestore, async (transaction) => {
      const orderSnap = await transaction.get(orderDoc);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }

      const order = { id: orderSnap.id, ...orderSnap.data() } as Order;
      const comments = order.comments ?? [];
      const commentIndex = comments.findIndex(c => c.id === commentId);
      
      if (commentIndex === -1) {
        throw new Error('Comment not found');
      }

      if (comments[commentIndex].userId !== user.uid) {
        throw new Error('Unauthorized to update this comment');
      }

      const updatedComments = [...comments];
      updatedComments[commentIndex] = {
        ...comments[commentIndex],
        message: newMessage.trim(),
        updatedAt: Timestamp.now()
      };

      transaction.update(orderDoc, {
        comments: updatedComments,
        lastUpdated: Timestamp.now()
      });
    });
  }

  async deleteComment(orderId: string, commentId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to delete comments');
    }

    const orderDoc = doc(this.firestore, 'orders', orderId);

    await runTransaction(this.firestore, async (transaction) => {
      const orderSnap = await transaction.get(orderDoc);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }

      const order = { id: orderSnap.id, ...orderSnap.data() } as Order;
      const comments = order.comments ?? [];
      const comment = comments.find(c => c.id === commentId);
      
      if (!comment) {
        throw new Error('Comment not found');
      }

      if (comment.userId !== user.uid) {
        throw new Error('Unauthorized to delete this comment');
      }

      const updatedComments = comments.filter(c => c.id !== commentId);

      transaction.update(orderDoc, {
        comments: updatedComments,
        lastUpdated: Timestamp.now()
      });
    });
  }

  async markCommentsAsSeen(orderId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    const orderDoc = doc(this.firestore, 'orders', orderId);
    await updateDoc(orderDoc, {
      [`commentsLastSeenBy.${user.uid}`]: Timestamp.now()
    });
  }

  getUnseenCommentsCount(order: Order, userId: string): number {
    if (!order.comments || order.comments.length === 0) return 0;
    
    const lastSeen = order.commentsLastSeenBy?.[userId];
    if (!lastSeen) return order.comments.length;
    
    return order.comments.filter(comment => 
      comment.createdAt.toMillis() > lastSeen.toMillis()
    ).length;
  }
}

const WEEK_OPTIONS = { weekStartsOn: 1 } as const;

type PricedOrder = Pick<Order, 'lastUpdated' | 'photos' | 'size' | 'promotion'>;

export const getTotalPhotos = (order?: Pick<Order, 'photos'>): number =>
  Object.values(order?.photos ?? {}).reduce((total, photo) => total + (photo.numberOfPrints ?? 0), 0);

export const calculateTotalPrice = (order?: Pick<Order, 'photos' | 'size' | 'promotion'>): number => {
  if (!order) return 0;

  const unitPrice = order.promotion?.discountPrice ?? order.size?.price ?? 0;
  return unitPrice * getTotalPhotos(order);
};

const BUCKETING = {
  day: { each: eachDayOfInterval, startOf: startOfDay, endOf: endOfDay },
  week: {
    each: (interval: { start: Date; end: Date }) => eachWeekOfInterval(interval, WEEK_OPTIONS),
    startOf: (date: Date) => startOfWeek(date, WEEK_OPTIONS),
    endOf: (date: Date) => endOfWeek(date, WEEK_OPTIONS)
  },
  month: { each: eachMonthOfInterval, startOf: startOfMonth, endOf: endOfMonth }
} satisfies Record<CountGranularity, {
  each: (interval: { start: Date; end: Date }) => Date[];
  startOf: (date: Date) => Date;
  endOf: (date: Date) => Date;
}>;

export const getCountGranularity = (from: Date, to: Date): CountGranularity => {
  const days = differenceInCalendarDays(to, from) + 1;
  if (days <= 92) return 'day';
  if (days <= 183) return 'week';
  return 'month';
};

export const getOrderPlatform = (order: Pick<Order, 'operatingSystem'>): OrderPlatform | null => {
  switch (order.operatingSystem?.toLowerCase()) {
    case 'android': return 'android';
    case 'ios':
    case 'ipados': return 'ios';
    default: return null;
  }
};

// Applies the platform, location and size filters in the browser and groups the result into
// zero-filled buckets whose size follows the range length. Changing those filters therefore
// needs no new fetch.
export const countPrintedOrders = (
  orders: readonly (PricedOrder & Pick<Order, 'operatingSystem' | 'deliveryLocationId'>)[],
  { from, to, platform, locationId, sizeKey }: PrintedOrdersQuery
): OrderCounts => {
  const granularity = getCountGranularity(from, to);
  const filtered = orders.filter(order =>
    (platform === 'all' || getOrderPlatform(order) === platform) &&
    (locationId === 'all' || order.deliveryLocationId === locationId) &&
    (sizeKey === 'all' || (!!order.size && getSizeKey(order.size) === sizeKey))
  );
  return { granularity, buckets: bucketOrders(filtered, from, to, granularity) };
};

// Groups orders by `lastUpdated` into zero-filled buckets covering [from, to].
// Bucket bounds are clamped to the range, so the first/last week or month may be partial.
export const bucketOrders = (
  orders: readonly PricedOrder[],
  from: Date,
  to: Date,
  granularity: CountGranularity
): OrderCountBucket[] => {
  const { each, startOf, endOf } = BUCKETING[granularity];
  const rangeStart = startOfDay(from);
  const rangeEnd = endOfDay(to);

  const buckets = each({ start: rangeStart, end: rangeEnd }).map(start => ({
    start: max([start, rangeStart]),
    end: min([endOf(start), rangeEnd]),
    count: 0,
    photos: 0,
    revenue: 0
  }));
  const bucketByStart = new Map(buckets.map(bucket => [startOf(bucket.start).getTime(), bucket]));

  for (const order of orders) {
    if (!order.lastUpdated) continue;
    const bucket = bucketByStart.get(startOf(order.lastUpdated.toDate()).getTime());
    if (!bucket) continue;

    bucket.count++;
    bucket.photos += getTotalPhotos(order);
    bucket.revenue += calculateTotalPrice(order);
  }
  return buckets;
};
